import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../lib/auth-context';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Skeleton } from '../../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Shield, Clock, Star, Users, AlertTriangle, RefreshCw, Eye, CheckCircle2, Mail, ChevronLeft, ChevronRight } from 'lucide-react';
import { formatDate, formatTime } from '../../lib/utils';
import SessionDetailModal from '../../components/SessionDetailModal';
import UserDetailModal from '../../components/UserDetailModal';
import { FinanceAuditTab } from '../../components/admin/FinanceAuditTab';

const PII_LABELS = { phone: 'Phone number', email: 'Email', instagram: 'Instagram', solicitation: 'Solicitation' };

const Pager = ({ page, setPage, total, limit, testid }) => {
  const pages = Math.max(1, Math.ceil(total / limit));
  if (pages <= 1) return null;
  return (
    <div className="flex items-center justify-end gap-2 mt-3" data-testid={testid}>
      <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid={`${testid}-prev`}>
        <ChevronLeft className="h-4 w-4" />
      </Button>
      <span className="text-xs text-muted-foreground">Page {page} of {pages}</span>
      <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage(page + 1)} data-testid={`${testid}-next`}>
        <ChevronRight className="h-4 w-4" />
      </Button>
    </div>
  );
};

const Kpi = ({ label, value, tone = '', testid }) => (
  <div className="p-3 rounded-xl border bg-card min-w-[120px]" data-testid={testid}>
    <p className={`text-2xl font-semibold ${tone}`}>{value ?? '—'}</p>
    <p className="text-xs text-muted-foreground mt-0.5">{label}</p>
  </div>
);

const ratingCls = (r) => r >= 8 ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
  : r >= 5 ? 'bg-amber-50 text-amber-700 border-amber-200'
    : 'bg-rose-50 text-rose-700 border-rose-200';

export default function AuditPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState('instructor');
  const [openSession, setOpenSession] = useState(null);
  const [openUserId, setOpenUserId] = useState(null);

  // PII flags
  const [pii, setPii] = useState(null);
  const [piiPage, setPiiPage] = useState(1);
  const [includeReviewed, setIncludeReviewed] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [expanded, setExpanded] = useState(null);
  // Late starts
  const [late, setLate] = useState(null);
  const [latePage, setLatePage] = useState(1);
  const [lateDays, setLateDays] = useState(30);
  // Feedback
  const [fb, setFb] = useState(null);
  const [fbPage, setFbPage] = useState(1);
  const [fbInstructor, setFbInstructor] = useState('all');
  // Attendance
  const [att, setAtt] = useState(null);
  const [attPage, setAttPage] = useState(1);

  const isSuper = user?.role === 'superadmin';

  const loadPii = useCallback(() => {
    api.get('/api/admin/audit/instructor/pii-flags', { params: { page: piiPage, limit: 10, include_reviewed: includeReviewed } })
      .then(r => setPii(r.data)).catch(() => toast.error('Failed to load PII flags'));
  }, [piiPage, includeReviewed]);

  const loadLate = useCallback(() => {
    api.get('/api/admin/audit/instructor/late-starts', { params: { days: lateDays, page: latePage, limit: 10 } })
      .then(r => setLate(r.data)).catch(() => toast.error('Failed to load late starts'));
  }, [lateDays, latePage]);

  useEffect(() => { if (isSuper && tab === 'instructor') { loadPii(); } }, [isSuper, tab, loadPii]);
  useEffect(() => { if (isSuper && tab === 'instructor') { loadLate(); } }, [isSuper, tab, loadLate]);
  useEffect(() => {
    if (isSuper && tab === 'feedback') {
      api.get('/api/admin/audit/feedback', { params: { page: fbPage, limit: 10, instructor_id: fbInstructor === 'all' ? undefined : fbInstructor } })
        .then(r => setFb(r.data)).catch(() => toast.error('Failed to load feedback audit'));
    }
  }, [isSuper, tab, fbPage, fbInstructor]);
  useEffect(() => {
    if (isSuper && tab === 'attendance') {
      api.get('/api/admin/audit/attendance', { params: { page: attPage, limit: 15 } })
        .then(r => setAtt(r.data)).catch(() => toast.error('Failed to load attendance audit'));
    }
  }, [isSuper, tab, attPage]);

  if (!isSuper) {
    return (
      <div className="p-8 text-center" data-testid="audit-restricted">
        <Shield className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
        <p className="font-medium">Restricted</p>
        <p className="text-sm text-muted-foreground">The Audit module is visible to Superadmins only.</p>
      </div>
    );
  }

  const scanNow = async () => {
    setScanning(true);
    try {
      const r = await api.post('/api/admin/audit/instructor/scan-now');
      toast.success(`Scanned ${r.data.scanned} classes — ${r.data.newly_flagged} new flag(s), ${r.data.emails_sent} email(s) sent`);
      loadPii();
    } catch { toast.error('Scan failed'); }
    setScanning(false);
  };

  const markReviewed = async (sid) => {
    try {
      await api.post(`/api/admin/audit/instructor/pii-flags/${sid}/review`);
      toast.success('Marked as reviewed');
      loadPii();
    } catch { toast.error('Failed to mark reviewed'); }
  };

  const slotLabel = (t, dur) => {
    if (!t) return '';
    const [h, m] = t.split(':').map(Number);
    const fmt = (d) => d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    return `${fmt(new Date(2000, 0, 1, h, m))} – ${fmt(new Date(2000, 0, 1, h, m + (dur || 60)))}`;
  };

  // Pass full row → SessionDetailModal header renders date/time from this prop
  const toSession = (r) => ({
    id: r.session_id, title: r.title || r.session_title,
    scheduled_date: r.scheduled_date, scheduled_time: r.scheduled_time,
    duration_minutes: r.duration_minutes,
  });

  return (
    <div className="space-y-4" data-testid="audit-page">
      <div>
        <h1 className="text-2xl font-semibold flex items-center gap-2"><Shield className="h-6 w-6" />Audit</h1>
        <p className="text-muted-foreground mt-1 text-sm">Instructor conduct, negative feedback and attendance risk — superadmin only</p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="instructor" data-testid="audit-tab-instructor"><Shield className="h-4 w-4 mr-1.5" />Instructor Audit</TabsTrigger>
          <TabsTrigger value="feedback" data-testid="audit-tab-feedback"><Star className="h-4 w-4 mr-1.5" />Feedback Audit</TabsTrigger>
          <TabsTrigger value="attendance" data-testid="audit-tab-attendance"><Users className="h-4 w-4 mr-1.5" />Attendance Audit</TabsTrigger>
          <TabsTrigger value="finance" data-testid="audit-tab-finance"><AlertTriangle className="h-4 w-4 mr-1.5" />Financial Audit</TabsTrigger>
        </TabsList>

        {/* ─────────── Tab 1: Instructor Audit ─────────── */}
        <TabsContent value="instructor" className="mt-4 space-y-4">
          <Card data-testid="pii-flags-card">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-rose-600" />Personal Info Sharing (PII Flags)
                </CardTitle>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => { setPiiPage(1); setIncludeReviewed(!includeReviewed); }} data-testid="pii-toggle-reviewed">
                    {includeReviewed ? 'Hide reviewed' : 'Show reviewed'}
                  </Button>
                  <Button size="sm" onClick={scanNow} disabled={scanning} data-testid="pii-scan-now-btn">
                    <RefreshCw className={`h-4 w-4 mr-1.5 ${scanning ? 'animate-spin' : ''}`} />{scanning ? 'Scanning…' : 'Scan now'}
                  </Button>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                Deterministic scanner over every class summary + full transcript — phone, email, Instagram, WhatsApp/Telegram solicitations.
                Each flagged class triggers ONE email to foundersoffice@soulsanskar.com.
                {pii?.stats?.last_scanned_at && <> Last scan: {new Date(pii.stats.last_scanned_at).toLocaleString()}.</>}
              </p>
            </CardHeader>
            <CardContent>
              {!pii ? <Skeleton className="h-40" /> : (
                <>
                  <div className="flex gap-3 mb-4 flex-wrap">
                    <Kpi label="Active flags" value={pii.stats?.active_flags} tone="text-rose-600" testid="kpi-pii-active" />
                    <Kpi label="Total flagged" value={pii.stats?.total_flagged} testid="kpi-pii-total" />
                    <Kpi label="Classes scanned" value={pii.stats?.total_scanned} testid="kpi-pii-scanned" />
                  </div>
                  {pii.flags?.length === 0 ? (
                    <p className="text-center text-muted-foreground py-6" data-testid="pii-empty">
                      No PII flags {includeReviewed ? '' : '(active)'} — all clear ✓
                    </p>
                  ) : (
                    <div className="space-y-3" data-testid="pii-flags-list">
                      {pii.flags.map(f => (
                        <div key={f.session_id} className={`p-4 rounded-xl border ${f.reviewed ? 'opacity-60' : 'border-rose-200 bg-rose-50/40'}`} data-testid={`pii-flag-${f.session_id}`}>
                          <div className="flex items-start justify-between gap-3 flex-wrap">
                            <div className="min-w-0">
                              <p className="text-sm font-medium">{f.instructor_name || 'Instructor not recorded'}</p>
                              <p className="text-xs text-muted-foreground">
                                {f.title}{f.batch_name ? ` · ${f.batch_name}` : ''} · {formatDate(f.scheduled_date)} {formatTime(f.scheduled_time)}
                              </p>
                              <div className="flex gap-1.5 mt-1.5 flex-wrap">
                                {f.categories.map(c => (
                                  <Badge key={c} variant="outline" className="bg-rose-50 text-rose-700 border-rose-200 text-[11px]">{PII_LABELS[c] || c}</Badge>
                                ))}
                                {f.emailed_at && <Badge variant="outline" className="text-[11px]"><Mail className="h-3 w-3 mr-1" />Emailed</Badge>}
                                {f.reviewed && <Badge variant="outline" className="bg-emerald-50 text-emerald-700 text-[11px]"><CheckCircle2 className="h-3 w-3 mr-1" />Reviewed</Badge>}
                              </div>
                            </div>
                            <div className="flex gap-2 shrink-0">
                              <Button variant="outline" size="sm" onClick={() => setExpanded(expanded === f.session_id ? null : f.session_id)} data-testid={`pii-expand-${f.session_id}`}>
                                <Eye className="h-4 w-4 mr-1" />{expanded === f.session_id ? 'Hide' : 'Evidence'}
                              </Button>
                              <Button variant="outline" size="sm" onClick={() => setOpenSession(toSession(f))} data-testid={`pii-open-session-${f.session_id}`}>Open session</Button>
                              {!f.reviewed && (
                                <Button size="sm" onClick={() => markReviewed(f.session_id)} data-testid={`pii-review-${f.session_id}`}>Mark reviewed</Button>
                              )}
                            </div>
                          </div>
                          {expanded === f.session_id && (
                            <div className="mt-3 space-y-2 border-t pt-3" data-testid={`pii-evidence-${f.session_id}`}>
                              {f.matches.map((m, i) => (
                                <div key={i} className="text-xs">
                                  <span className="font-medium text-rose-700">{PII_LABELS[m.type] || m.type}</span>
                                  <span className="text-muted-foreground"> (in {m.source})</span>:
                                  <code className="mx-1 px-1.5 py-0.5 rounded bg-rose-100 text-rose-800">{m.matched}</code>
                                  <p className="text-muted-foreground mt-0.5 italic">“…{m.snippet}…”</p>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                      <Pager page={piiPage} setPage={setPiiPage} total={pii.total} limit={10} testid="pii-pager" />
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          <Card data-testid="late-starts-card">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <CardTitle className="text-lg flex items-center gap-2"><Clock className="h-5 w-5 text-amber-600" />Late Starts (&gt;2 min)</CardTitle>
                <div className="flex gap-1.5">
                  {[7, 30, 90].map(d => (
                    <Button key={d} variant={lateDays === d ? 'default' : 'outline'} size="sm"
                      onClick={() => { setLatePage(1); setLateDays(d); }} data-testid={`late-days-${d}`}>{d}d</Button>
                  ))}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {!late ? <Skeleton className="h-40" /> : (
                <>
                  <div className="flex gap-3 mb-4 flex-wrap">
                    <Kpi label={`Late sessions (${late.days}d)`} value={late.stats?.total_late} tone="text-amber-600" testid="kpi-late-total" />
                    <Kpi label="Avg delay (min)" value={late.stats?.avg_delay_minutes} testid="kpi-late-avg" />
                    <Kpi label="Worst delay (min)" value={late.stats?.worst_delay_minutes} testid="kpi-late-worst" />
                  </div>
                  {late.late_sessions?.length === 0 ? (
                    <p className="text-center text-muted-foreground py-6" data-testid="late-empty">No late starts in the last {late.days} days ✓</p>
                  ) : (
                    <>
                      <Table data-testid="late-starts-table">
                        <TableHeader>
                          <TableRow>
                            <TableHead>Date</TableHead><TableHead>Class</TableHead><TableHead>Instructor</TableHead>
                            <TableHead>Batch</TableHead><TableHead>Scheduled</TableHead><TableHead>Started (IST)</TableHead><TableHead>Delay</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {late.late_sessions.map(s => (
                            <TableRow key={s.session_id} className="cursor-pointer hover:bg-muted/40"
                              onClick={() => setOpenSession(toSession(s))} data-testid={`late-row-${s.session_id}`}>
                              <TableCell className="whitespace-nowrap">{formatDate(s.scheduled_date)}</TableCell>
                              <TableCell className="font-medium">{s.title}</TableCell>
                              <TableCell>{s.instructor_name || '—'}</TableCell>
                              <TableCell>{s.batch_name || '—'}</TableCell>
                              <TableCell>{formatTime(s.scheduled_time)}</TableCell>
                              <TableCell>{formatTime(s.actual_start_ist)}</TableCell>
                              <TableCell>
                                <Badge variant="outline" className={s.delay_minutes >= 10 ? 'bg-rose-50 text-rose-700 border-rose-200' : 'bg-amber-50 text-amber-700 border-amber-200'}>
                                  +{s.delay_minutes} min
                                </Badge>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                      <Pager page={latePage} setPage={setLatePage} total={late.total} limit={10} testid="late-pager" />
                    </>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ─────────── Tab 2: Feedback Audit ─────────── */}
        <TabsContent value="feedback" className="mt-4">
          <Card data-testid="feedback-audit-card">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <CardTitle className="text-lg flex items-center gap-2"><Star className="h-5 w-5 text-rose-600" />Negative Feedback (rating &lt; 7/10)</CardTitle>
                <Select value={fbInstructor} onValueChange={(v) => { setFbPage(1); setFbInstructor(v); }}>
                  <SelectTrigger className="w-[220px]" data-testid="fb-instructor-filter"><SelectValue placeholder="All instructors" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All instructors</SelectItem>
                    {(fb?.instructors || []).map(i => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              {!fb ? <Skeleton className="h-40" /> : (
                <>
                  <div className="flex gap-3 mb-4 flex-wrap">
                    <Kpi label="Negative reviews" value={fb.stats?.negative_total} tone="text-rose-600" testid="kpi-fb-negative" />
                    <Kpi label="Total reviews" value={fb.stats?.total_reviews} testid="kpi-fb-total" />
                    <Kpi label="Avg rating (all)" value={fb.stats?.avg_rating} testid="kpi-fb-avg" />
                    <Kpi label="% negative" value={`${fb.stats?.negative_pct ?? 0}%`} testid="kpi-fb-pct" />
                  </div>
                  {fb.feedback?.length === 0 ? (
                    <p className="text-center text-muted-foreground py-6" data-testid="fb-empty">No negative feedback ✓</p>
                  ) : (
                    <div className="space-y-3" data-testid="fb-list">
                      {fb.feedback.map(f => (
                        <div key={f.id} className="p-4 rounded-xl border hover:bg-muted/20" data-testid={`fb-row-${f.id}`}>
                          <div className="flex items-start justify-between gap-3 flex-wrap">
                            <div className="min-w-0">
                              <p className="text-sm font-medium">{f.learner_name || 'Learner'} <span className="text-muted-foreground font-normal">on</span> {f.instructor_name || 'instructor'}</p>
                              <p className="text-xs text-muted-foreground">
                                {f.batch_name || f.session_title} · {f.scheduled_date ? formatDate(f.scheduled_date) : '—'}
                                {f.scheduled_time ? ` · ${slotLabel(f.scheduled_time, f.duration_minutes)}` : ''}
                              </p>
                              {f.comments && <p className="text-sm mt-1.5 italic text-foreground/90">“{f.comments}”</p>}
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              <Badge variant="outline" className={`${ratingCls(f.rating)} font-semibold`}>
                                <Star className="h-3 w-3 mr-1 fill-current" />{f.rating}/10
                              </Badge>
                              {f.session_id && (
                                <Button variant="outline" size="sm" onClick={() => setOpenSession(toSession(f))} data-testid={`fb-open-session-${f.id}`}>Open session</Button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                      <Pager page={fbPage} setPage={setFbPage} total={fb.total} limit={10} testid="fb-pager" />
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ─────────── Tab 3: Attendance Audit ─────────── */}
        <TabsContent value="attendance" className="mt-4">
          <Card data-testid="attendance-audit-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2"><Users className="h-5 w-5 text-blue-600" />Low Attendance (&lt;60%)</CardTitle>
              <p className="text-xs text-muted-foreground">
                Active learners only, counted from each learner's batch-assignment date, minimum {att?.stats?.min_sessions ?? 4} classes held.
                Delivered moms are excluded unless they joined MTTF- Postnatal Yoga (attendance restarts fresh there).
              </p>
            </CardHeader>
            <CardContent>
              {!att ? <Skeleton className="h-40" /> : (
                <>
                  <div className="flex gap-3 mb-4 flex-wrap">
                    <Kpi label="Flagged learners" value={att.stats?.flagged} tone="text-rose-600" testid="kpi-att-flagged" />
                    <Kpi label="Learners reviewed" value={att.stats?.considered} testid="kpi-att-considered" />
                    <Kpi label="Excluded (delivered)" value={att.stats?.excluded_delivered} testid="kpi-att-excluded" />
                    <Kpi label="Avg % of flagged" value={`${att.stats?.avg_pct_of_flagged ?? 0}%`} testid="kpi-att-avg" />
                  </div>
                  {att.learners?.length === 0 ? (
                    <p className="text-center text-muted-foreground py-6" data-testid="att-empty">No learners under 60% ✓</p>
                  ) : (
                    <>
                      <Table data-testid="attendance-table">
                        <TableHeader>
                          <TableRow>
                            <TableHead>Learner</TableHead><TableHead>Stage</TableHead><TableHead>Batch(es)</TableHead>
                            <TableHead>Attendance</TableHead><TableHead>Attended / Held</TableHead><TableHead>Last attended</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {att.learners.map(l => (
                            <TableRow key={l.user_id} className="cursor-pointer hover:bg-muted/40"
                              onClick={() => setOpenUserId(l.user_id)} data-testid={`att-row-${l.user_id}`}>
                              <TableCell>
                                <p className="font-medium">{l.name}</p>
                                <p className="text-xs text-muted-foreground">{l.email}</p>
                              </TableCell>
                              <TableCell><Badge variant="outline" className="text-[11px]">{l.stage}</Badge></TableCell>
                              <TableCell className="text-xs">{l.batches.join(', ')}</TableCell>
                              <TableCell>
                                <Badge variant="outline" className={l.pct < 30 ? 'bg-rose-50 text-rose-700 border-rose-200 font-semibold' : 'bg-amber-50 text-amber-700 border-amber-200 font-semibold'}>
                                  {l.pct}%
                                </Badge>
                              </TableCell>
                              <TableCell>{l.attended} / {l.held}</TableCell>
                              <TableCell>{l.last_attended ? formatDate(l.last_attended) : <span className="text-rose-600 text-xs font-medium">Never</span>}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                      <Pager page={attPage} setPage={setAttPage} total={att.total} limit={15} testid="att-pager" />
                    </>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* V54 — Financial Audit: currency mismatches (intl lead charged INR, etc.) */}
        <TabsContent value="finance" className="mt-4">
          {tab === 'finance' && <FinanceAuditTab onOpenUser={setOpenUserId} />}
        </TabsContent>
      </Tabs>

      <SessionDetailModal session={openSession} open={!!openSession} onClose={() => setOpenSession(null)} onUpdate={() => {}} onEdit={() => {}} />
      <UserDetailModal userId={openUserId} open={!!openUserId} onClose={() => setOpenUserId(null)} onUpdate={() => {}} />
    </div>
  );
}
